import React, { useState, useRef, useCallback } from 'react';
import { connect } from 'redux-zero/react';
import { combineActions } from 'redux-zero/utils';
import { useTranslation } from 'react-i18next';
import { useAsync, useClickAway } from 'react-use';
import {
  DsButton,
  DsDialog,
  DsIcon,
  DsLoader,
  DsExplorerLink,
  DsCard,
  DsTooltip,
} from '@tokenyico/design-system/dist/reactJS';
import { dsConfirm } from '@tokenyico/design-system';
import { formActions } from 'app/state/actions';
import { holderActions } from 'app/state/actions';
import { CentralizedExchangeService } from 'services';
import { HolderService } from 'services';
import AddDepositWalletDialog from './AddDepositWalletDialog';
import SendTokensDialog from './SendTokensDialog';
import CEXStatus from './CEXStatus';

import './CentralizedExchange.less';

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="empty-state">
      <span className="_m:bottom(4)">{t('emptyState.noCexs.body')}</span>
      <DsButton
        label={t('emptyState.noCexs.helpButton')}
        type="button"
        version="monochrome"
        icon="message"
        iconColor="inherit"
        iconSize="1em"
        isReverse
      />
    </div>
  );
};

const SendTokensButton = ({ props: { balance, cex, handleOpenSendTokensDialog } }) => {
  const { t } = useTranslation();

  //sorted by priority
  const configs = [
    { tooltipText: t('shared.errorCEXNotAllowed'), isDisabled: !cex?.isCexAllowed },
    { tooltipText: t('shared.errorInsufficientBalance'), isDisabled: !balance },
    { tooltipText: t('shared.errorPausedToken'), isDisabled: balance?.token.isPaused },
  ];

  const { tooltipText, isDisabled } = configs.find(({ isDisabled }) => isDisabled) || {
    tooltipText: '',
    isDisabled: false,
  };

  return (
    <DsTooltip text={tooltipText} isVisibleOnHover={isDisabled}>
      <DsButton
        label={t('shared.sendTokens')}
        type="button"
        version="primary"
        isMini
        onClick={handleOpenSendTokensDialog}
        isDisabled={isDisabled}
      />
    </DsTooltip>
  );
};

const CEXCard = ({
  cex,
  balance,
  state: {
    selectedToken,
    setSelectedCex,
    setSuccess,
    setError,
    addDepositWalletDialogRef,
    dialogSendTokensRef,
  },
  onRefresh,
}) => {
  const { t } = useTranslation();

  const cancelButtonRef = useRef();

  const [showCancelButton, setShowCancelButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useClickAway(cancelButtonRef, () => {
    setShowCancelButton(false);
  });

  const handleCancelRequest = async () => {
    setShowCancelButton(false);
    if (
      await dsConfirm({
        heading: t('shared.cancelDepositWalletRequest'),
        content: t('shared.cancelDepositWalletRequestContent'),
        action: `${t('shared.yes')}, ${t('shared.cancelRequest')}`,
        version: 'danger',
      })
    ) {
      return handleCancelConfirmed();
    }
  };

  const handleCancelConfirmed = async () => {
    setIsSubmitting(true);
    try {
      await CentralizedExchangeService.deleteDepositWallet({
        cexId: cex.id,
        tokenId: selectedToken.id,
        depositWalletId: cex.depositWalletId,
      });

      setSuccess(t('shared.operationSuccessful'));
      await onRefresh();
    } catch (error) {
      setError(error.message);
    }
    setIsSubmitting(false);
  };

  const handleOpenAddDepositWalletDialog = () => {
    setSelectedCex(cex);
    addDepositWalletDialogRef.current.open();
  };

  const handleOpenSendTokensDialog = () => {
    setSelectedCex(cex);
    dialogSendTokensRef.current.open();
  };

  return (
    <div className="card">
      <img height="64" src={cex.logo || selectedToken.image} />
      <a className="cex-link" href={cex.url} target="_blank" rel="noreferrer">
        <DsIcon name="external-link" size="1em" />
        <span className="cex-link__display">{cex.url}</span>
      </a>
      <hr />
      <div className="card__actions">
        <div className="card__actions--top">
          {t('shared.depositWallet')}
          {cex.depositWalletStatus === 'PENDING' && (
            <div className="card__actions__pending">
              <CEXStatus status={cex.depositWalletStatus} />
              {isSubmitting ? (
                <DsIcon name="loader-circle" size="15px" color="var(--primary)" />
              ) : (
                <>
                  <span
                    className="card__actions__options"
                    onClick={() => setShowCancelButton((state) => !state)}
                  >
                    <DsIcon name="options" size="15px" />
                  </span>
                  {showCancelButton && (
                    <div className="card__actions__cancel">
                      <DsCard ref={cancelButtonRef}>
                        <DsButton
                          label={t('shared.cancelRequest')}
                          type="button"
                          version="error"
                          icon="close"
                          iconSize="1em"
                          isReverse
                          isMini
                          onClick={handleCancelRequest}
                        />
                      </DsCard>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {cex.depositWalletStatus === 'APPROVED' && (
            <div>
              <SendTokensButton props={{ balance, cex, handleOpenSendTokensDialog }} />
            </div>
          )}
          {!['PENDING', 'APPROVED'].includes(cex.depositWalletStatus) && (
            <CEXStatus status={cex.depositWalletStatus} />
          )}
        </div>
        {cex.depositWalletAddress && (
          <DsExplorerLink
            className="_m:top(2)"
            address={cex.depositWalletAddress}
            network={selectedToken.blockchainNetwork.toLowerCase()}
            type="wallet"
            width="190"
            isCopyable
          />
        )}
        {!cex.depositWalletId && (
          <DsButton
            version="primary"
            icon="wallet"
            label={t('shared.addDepositWallet')}
            isReverse
            onClick={handleOpenAddDepositWalletDialog}
            isDisabled={!cex.isCexAllowed}
          />
        )}
      </div>
    </div>
  );
};

const CentralizedExchangesView = ({ selectedToken, setError, setSuccess, setBalances }) => {
  const addDepositWalletDialogRef = useRef();
  const dialogSendTokensRef = useRef();

  const [cexs, setCexs] = useState();
  const [balance, setBalance] = useState(null);
  const [selectedCex, setSelectedCex] = useState(null);

  const state = {
    selectedToken,
    addDepositWalletDialogRef,
    dialogSendTokensRef,
    setSelectedCex,
    setError,
    setSuccess,
  };

  const fetchCexs = useCallback(
    async (selectedToken) => {
      try {
        const data = await CentralizedExchangeService.getCEXsFromToken(selectedToken.id);
        setCexs(data);
      } catch (e) {
        setError(e);
      }
    },
    [setError, selectedToken],
  );

  const fetchBalances = useCallback(
    async (selectedToken) => {
      try {
        const newbalances = await HolderService.getBalances(selectedToken.id);
        setBalance(newbalances[0]);
        setBalances(newbalances);
      } catch (e) {
        setError(e);
      }
    },
    [setError, selectedToken],
  );

  const request = useAsync(async () => {
    await fetchCexs(selectedToken);
    await fetchBalances(selectedToken);
  });

  const handleReload = async () => {
    await fetchCexs(selectedToken);
  };
  const onCloseDialog = async (ref) => {
    ref.current.close();
    setSelectedCex(null);
    await handleReload();
  };
  const handleCloseAddDepositWalletDialog = () => onCloseDialog(addDepositWalletDialogRef);
  const handleCloseSendTokensDialog = () => onCloseDialog(dialogSendTokensRef);

  return (
    <div id="centralized-exchange">
      <div className="cards-container _col(12)">
        {request.loading ? (
          <div className="_p(4) _m:x(auto)">
            <DsLoader version="dots" color="var(--blue-500)" />
          </div>
        ) : (
          <>
            {cexs.length ? (
              cexs.map((cex) => (
                <CEXCard
                  key={cex.id}
                  cex={cex}
                  balance={balance}
                  state={state}
                  onRefresh={handleReload}
                />
              ))
            ) : (
              <EmptyState />
            )}
          </>
        )}
        <DsDialog
          isModal={true}
          ref={addDepositWalletDialogRef}
          isDismissible={true}
          onClosed={() => setSelectedCex(null)}
        >
          <AddDepositWalletDialog
            cex={selectedCex}
            state={state}
            onCloseModal={handleCloseAddDepositWalletDialog}
          />
        </DsDialog>
        <DsDialog
          isModal={true}
          ref={dialogSendTokensRef}
          isDismissible={true}
          onClosed={() => setSelectedCex(null)}
        >
          <SendTokensDialog
            cex={selectedCex}
            state={state}
            dialogRef={dialogSendTokensRef}
            onCloseModal={handleCloseSendTokensDialog}
          />
        </DsDialog>
      </div>
    </div>
  );
};

const mapToProps = ({ holder: { selectedToken } }) => ({ selectedToken });

export default connect(
  mapToProps,
  combineActions(formActions, holderActions),
)(CentralizedExchangesView);
